import { useState, useEffect, useCallback, useRef } from 'react';

export const useInfiniteScroll = (fetchData, dependencies = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  
  // Store dependencies in a ref to prevent unnecessary rerenders
  const depsRef = useRef(dependencies);
  depsRef.current = dependencies;

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData(nextCursor);
      
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format');
      }

      const newData = response.data || [];
      const newCursor = response.next_cursor;

      setData(prev => [...prev, ...newData]);
      setNextCursor(newCursor);
      setHasMore(!!newCursor);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [fetchData, nextCursor, loading, hasMore]);

  const reset = useCallback(() => {
    setData([]);
    setNextCursor(null);
    setHasMore(true);
    setError(null);
    setLoading(false);
  }, []);

  // Reset when dependencies change
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Load initial data
  useEffect(() => {
    if (!loading && data.length === 0 && hasMore) {
      loadMore();
    }
  }, [loading, data.length, hasMore, loadMore]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset
  };
};
